import { createSlice } from '@reduxjs/toolkit';
import { fetchComplaintSummary, fetchStatuses } from './actions';

const initialState = {
  list: [],
  isLoading: false,
  error: null,
  hasLoaded: false,
  requestedComplaintTypeId: null,
  complaintSummary: null,
  isComplaintSummaryLoading: false,
  complaintSummaryError: null,
  hasComplaintSummaryLoaded: false,
  requestedComplaintSummaryTypeId: null,
};

const unwrapStatuses = (payload) => {
  const data = payload?.data ?? payload?.results ?? payload?.items ?? payload;
  return Array.isArray(data) ? data : [];
};

const unwrapComplaintSummary = (payload) => {
  const data = payload?.data ?? payload;
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  return data;
};

const errorMessage = (action) =>
  action.payload?.message ?? action.error?.message ?? 'Failed to fetch statuses';

const complaintSummaryErrorMessage = (action) =>
  action.payload?.message ?? action.error?.message ?? 'Failed to fetch complaint summary';

const isStaleRequest = (stateId, requestedId) =>
  stateId != null &&
  requestedId != null &&
  String(stateId) !== String(requestedId);

const statusSlice = createSlice({
  name: 'status',
  initialState,
  reducers: {
    // Unused: nothing dispatches this helper.
    // clearStatusError: (state) => {
    //   state.error = null;
    // },
    resetStatusState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStatuses.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
        state.hasLoaded = false;
        state.list = [];
        state.requestedComplaintTypeId = action.meta.arg ?? null;
      })
      .addCase(fetchStatuses.fulfilled, (state, action) => {
        const requestedId = action.meta.arg;
        if (isStaleRequest(state.requestedComplaintTypeId, requestedId)) {
          return;
        }
        const list = unwrapStatuses(action.payload);
        state.isLoading = false;
        state.hasLoaded = true;
        state.list = [...list].sort(
          (left, right) => (left.displayOrder ?? 0) - (right.displayOrder ?? 0),
        );
      })
      .addCase(fetchStatuses.rejected, (state, action) => {
        if (action.meta.aborted) return;
        const requestedId = action.meta.arg;
        if (isStaleRequest(state.requestedComplaintTypeId, requestedId)) {
          return;
        }
        state.isLoading = false;
        state.hasLoaded = true;
        state.error = errorMessage(action);
      })
      .addCase(fetchComplaintSummary.pending, (state, action) => {
        state.isComplaintSummaryLoading = true;
        state.complaintSummaryError = null;
        state.hasComplaintSummaryLoaded = false;
        state.complaintSummary = null;
        state.requestedComplaintSummaryTypeId = action.meta.arg ?? null;
      })
      .addCase(fetchComplaintSummary.fulfilled, (state, action) => {
        const requestedId = action.meta.arg;
        if (isStaleRequest(state.requestedComplaintSummaryTypeId, requestedId)) {
          return;
        }
        state.isComplaintSummaryLoading = false;
        state.hasComplaintSummaryLoaded = true;
        state.complaintSummary = unwrapComplaintSummary(action.payload);
      })
      .addCase(fetchComplaintSummary.rejected, (state, action) => {
        if (action.meta.aborted) return;
        const requestedId = action.meta.arg;
        if (isStaleRequest(state.requestedComplaintSummaryTypeId, requestedId)) {
          return;
        }
        state.isComplaintSummaryLoading = false;
        state.hasComplaintSummaryLoaded = true;
        state.complaintSummaryError = complaintSummaryErrorMessage(action);
      });
  },
});

export const {
  // clearStatusError,
  resetStatusState,
} = statusSlice.actions;
export default statusSlice.reducer;
